// ExportModal — PDF-Export-Trigger + Last-Exports-Liste (Plan §S6.6).
// Auf "Export starten" wird POST /api/boards/:id/export gefeuert, die Response landet
// als Eintrag oben in der Liste. Download per Klick auf "board.pdf".

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ExportSummaryDto, exportApi } from "./api.js";
import "./ExportModal.css";

interface ExportModalProps {
  open: boolean;
  boardId: string;
  boardName?: string | null;
  onClose: () => void;
}

type ExportError = { code: "trigger" | "list"; message: string };

// Download-Dateiname: <Tagesdatum>_<Board-Name>.pdf. Datum aus triggeredAt in lokaler Zeit,
// Board-Name von Windows-unzulaessigen Pfadzeichen befreit (das `download`-Attribut greift,
// weil die signierte URL same-origin ist).
function exportFilename(boardName: string | null | undefined, triggeredAt: string): string {
  const d = new Date(triggeredAt);
  const date = Number.isNaN(d.getTime())
    ? ""
    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const name = (boardName ?? "").replace(/[<>:"/\\|?*]/g, "_").trim() || "board";
  return date ? `${date}_${name}.pdf` : `${name}.pdf`;
}

export function ExportModal({
  open,
  boardId,
  boardName,
  onClose,
}: ExportModalProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [exports, setExports] = useState<ExportSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<ExportError | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await exportApi.list(boardId);
      setExports(list);
    } catch (err) {
      setError({ code: "list", message: err instanceof Error ? err.message : String(err) });
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  // Polish P1: poll waehrend ein Export status=running hat, sonst Idle.
  const hasRunning = exports.some((e) => e.status === "running");
  useEffect(() => {
    if (!open || !hasRunning) return;
    const interval = setInterval(() => {
      void refresh();
    }, 1500);
    return () => clearInterval(interval);
  }, [open, hasRunning, refresh]);

  const onTrigger = useCallback(async (): Promise<void> => {
    setTriggering(true);
    setError(null);
    try {
      const result = await exportApi.trigger(boardId);
      setExports((prev) => [result, ...prev.filter((e) => e.id !== result.id)]);
    } catch (err) {
      setError({ code: "trigger", message: err instanceof Error ? err.message : String(err) });
    } finally {
      setTriggering(false);
    }
  }, [boardId]);

  // Polish P2: signed URLs werden vom Server pro Eintrag mitgegeben — Anchor reicht.

  const onDialogClick = (e: React.MouseEvent<HTMLDialogElement>): void => {
    if (e.target === dialogRef.current) onClose();
  };

  const fmt = (iso: string | null): string => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(i18n.language);
    } catch {
      return iso;
    }
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: natives <dialog>, Escape selbst
    <dialog
      ref={dialogRef}
      className="export-modal-dialog"
      onClose={onClose}
      onClick={onDialogClick}
    >
      <form method="dialog" className="export-modal-form">
        <header className="export-modal-header">
          <h2>{t("export.modal.title")}</h2>
          <button
            type="button"
            className="export-modal-close"
            onClick={onClose}
            aria-label={t("export.modal.close")}
          >
            ×
          </button>
        </header>

        <section className="export-modal-section">
          <h3 className="export-modal-section-title">{t("export.modal.formatLabel")}</h3>
          <div className="export-modal-formats">
            <div className="export-modal-format is-active">
              <span className="export-modal-format-name">{t("export.format.pdf")}</span>
              <span className="export-modal-format-hint">{t("export.format.pdfHint")}</span>
            </div>
          </div>
          <div className="export-modal-actions">
            <button
              type="button"
              className="export-modal-trigger"
              onClick={onTrigger}
              disabled={triggering}
            >
              {triggering ? t("export.modal.running") : t("export.modal.trigger")}
            </button>
          </div>
          {error && (
            <p className="export-modal-error" role="alert">
              {t(`export.error.${error.code}`)}: {error.message}
            </p>
          )}
        </section>

        <section className="export-modal-section">
          <header className="export-modal-list-header">
            <h3 className="export-modal-section-title">{t("export.modal.recentTitle")}</h3>
            <button
              type="button"
              className="export-modal-refresh"
              onClick={() => void refresh()}
              disabled={loading}
            >
              {loading ? t("export.modal.loading") : t("export.modal.refresh")}
            </button>
          </header>
          {exports.length === 0 ? (
            <p className="export-modal-empty">{t("export.modal.empty")}</p>
          ) : (
            <ul className="export-modal-list">
              {exports.map((entry) => (
                <li key={entry.id} className={`export-modal-row is-${entry.status}`}>
                  <div className="export-modal-row-meta">
                    <span className="export-modal-row-ts">{fmt(entry.triggeredAt)}</span>
                    <span className={`export-modal-status status-${entry.status}`}>
                      {t(`export.status.${entry.status}`)}
                    </span>
                    <span className="export-modal-row-format">{entry.format.toUpperCase()}</span>
                  </div>
                  {entry.status === "done" ? (
                    <div className="export-modal-row-downloads">
                      {entry.downloads.map((d) => (
                        <a
                          key={d.file}
                          className="export-modal-download"
                          href={d.url}
                          download={exportFilename(boardName, entry.triggeredAt)}
                        >
                          {d.file}
                        </a>
                      ))}
                    </div>
                  ) : entry.status === "failed" && entry.error ? (
                    <p className="export-modal-row-error">{entry.error}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </form>
    </dialog>
  );
}
